import { useEffect, useState } from "react";
import { SlaDashboardSummary, SlaActiveFilters } from "../types";
import axios from "v2/utils/axios";

interface UseSlaDashboardResult {
  summary: SlaDashboardSummary | null;
  isLoading: boolean;
  unauthorized: boolean;
}

export function useSlaDashboard(filters: SlaActiveFilters, propertyId?: string): UseSlaDashboardResult {
  const [summary, setSummary] = useState<SlaDashboardSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setUnauthorized(false);
    const params: Record<string, any> = {};

    if (filters.dateRangePreset) {
      params.date_range_preset = filters.dateRangePreset;
    } else {
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;
    }

    if (filters.teamMemberIds && filters.teamMemberIds.length > 0) {
      params.team_member_ids = filters.teamMemberIds.join(",");
    }

    // api-contract §5.1 / D0.6 [Corrects breakdown] — "all" is gone; always
    // send the resolved lifecycle so the summary matches the selected section.
    if (filters.taskLifecycleStatus) {
      params.task_lifecycle_status = filters.taskLifecycleStatus;
    }

    const path = propertyId
      ? `${process.env.REACT_APP_API_ENDPOINT_V2}properties/${propertyId}/sla_dashboard`
      : `${process.env.REACT_APP_API_ENDPOINT_V2}sla_dashboard`;

    axios
      .get(path, { params })
      .then((res) => {
        const data = res.data?.data ?? res.data;
        setSummary(data as SlaDashboardSummary);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          setUnauthorized(true);
        } else {
          console.error("Error fetching SLA dashboard:", err);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    filters.dateRangePreset,
    filters.startDate,
    filters.endDate,
    filters.teamMemberIds,
    filters.taskLifecycleStatus,
    propertyId,
  ]);

  return { summary, isLoading, unauthorized };
}
